import { umToMeters } from "@buildwithflux/shared";
import { round } from "mathjs";
import { edgeCoupledMicrostrip, edgeCoupledStripline, embeddedMicrostrip, microstrip, symetricStripline, } from "./impedanceEquations";
export var StripType;
(function (StripType) {
    StripType["MICROSTRIP"] = "microstrip";
    StripType["EMBEDDED_MICROSTRIP"] = "embedded-microstrip";
    StripType["EDGE_COUPLED_MICROSTRIP"] = "edge-coupled-microstrip";
    StripType["EDGE_COUPLED_STRIPLINE"] = "edge-coupled-stripline";
    StripType["SYMMETRIC_STRIPLINE"] = "symmetric-stripline";
})(StripType || (StripType = {}));
export const IMPEDANCE_WIDTH_MIN = 125; // um
const IMPEDANCE_WIDTH_MAX = 1000; // um
export const IMPEDANCE_SPACING_MIN = 125; // um
const IMPEDANCE_SPACING_MAX = 1000;
const STEP_SIZE = 5; //um
/**
 * Calculate the impedance of a trace given its width, spacing, and all other
 * parameters.
 *
 * w: trace width
 * s: trace spacing
 * w_min: minimum trace width
 * s_min: minimum trace spacing
 * Z(w, s): impedance of trace given width, spacing, and all other parameters
 *
 * 1. Set w = w_min, s = s_min (or 125um if not specified)
 * 2. Compute Z_initial = Z(w_min, s_min)
 * 3. If Z_initial < Z_target, leave w = w_min and find s = s_opt s.t. Z(w_min, s)
 * 4. If Z_initial > Z_target, leave s = s_min and find w = w_opt s.t. Z(w,
 *    s_min) = Z_target
 * 5. If w_opt is larger than w_max, there is no solution and the user must either
 *    choose larger maxima or change their stackup.
 * 6. If s_opt is larger than s_max, then there's no solution and again the user
 *    must either choose larger maxima or change their stackup.
 */
export function findWidthAndSpacing(targetImpedance, impedanceConfig) {
    const impedance = calculateImpedance({
        ...impedanceConfig,
        width: IMPEDANCE_WIDTH_MIN,
        spacing: IMPEDANCE_SPACING_MIN,
    });
    let widthOpt = IMPEDANCE_WIDTH_MIN;
    let spacingOpt = IMPEDANCE_SPACING_MIN;
    // Our impedance matches with the default parameters, so we can just return the minimums
    if (impedance === targetImpedance) {
        return {
            width: umToMeters(IMPEDANCE_WIDTH_MIN),
            spacing: umToMeters(IMPEDANCE_SPACING_MIN),
        };
    }
    if (targetImpedance > impedance) {
        // Incrementing spacing will increase the impedance
        spacingOpt = findSpacing(targetImpedance, impedanceConfig);
    }
    else if (impedance > targetImpedance) {
        // Incrementing width will decrease the impedance
        widthOpt = findWidth(targetImpedance, impedanceConfig);
    }
    return {
        width: umToMeters(widthOpt),
        spacing: umToMeters(spacingOpt),
    };
}
/*
 * Find the closest width within our min/max constraints that will give us the target impedance
 */
function findWidth(targetImpedance, impedanceConfig) {
    const { spacing } = impedanceConfig;
    let lastClosestDiff = Infinity;
    let bestWidth = IMPEDANCE_WIDTH_MIN;
    for (let currentWidth = IMPEDANCE_WIDTH_MIN; currentWidth <= IMPEDANCE_WIDTH_MAX + STEP_SIZE; currentWidth += STEP_SIZE) {
        const impedance = calculateImpedance({ ...impedanceConfig, width: currentWidth, spacing });
        // The previous result was the closest we could get to the target impedance so we can return it
        const diff = Math.abs(impedance - targetImpedance);
        if (diff < lastClosestDiff) {
            bestWidth = currentWidth;
        }
        lastClosestDiff = diff;
    }
    if (bestWidth > IMPEDANCE_WIDTH_MAX) {
        throw new Error("No solution exists");
    }
    return bestWidth;
}
/*
 * Find the closest spacing within our min/max constraints that will give us the target impedance
 */
function findSpacing(targetImpedance, impedanceConfig) {
    const { width } = impedanceConfig;
    let lastClosestDiff = Infinity;
    let bestSpacing = IMPEDANCE_SPACING_MIN;
    for (let currentSpacing = IMPEDANCE_SPACING_MIN; currentSpacing <= IMPEDANCE_SPACING_MAX + STEP_SIZE; currentSpacing += STEP_SIZE) {
        const impedance = calculateImpedance({ ...impedanceConfig, width, spacing: currentSpacing });
        // The previous result was the closest we could get to the target impedance so we can return it
        const diff = Math.abs(impedance - targetImpedance);
        if (diff < lastClosestDiff) {
            bestSpacing = currentSpacing;
        }
        lastClosestDiff = diff;
    }
    if (bestSpacing > IMPEDANCE_SPACING_MAX) {
        throw new Error("No solution exists");
    }
    return bestSpacing;
}
/**
 * Calculate the impedance for a given set of parameters based on the strip type.
 * @returns the impedance to the nearest ohm
 */
export function calculateImpedance(impedanceConfig) {
    if (impedanceConfig.type === StripType.MICROSTRIP) {
        return round(microstrip(impedanceConfig).zo, 2);
    }
    else if (impedanceConfig.type === StripType.EMBEDDED_MICROSTRIP) {
        return round(embeddedMicrostrip(impedanceConfig).zo, 2);
    }
    else if (impedanceConfig.type === StripType.EDGE_COUPLED_MICROSTRIP) {
        return round(edgeCoupledMicrostrip(impedanceConfig).zoDiff, 2);
    }
    else if (impedanceConfig.type === StripType.EDGE_COUPLED_STRIPLINE) {
        return round(edgeCoupledStripline(impedanceConfig).zoDiff, 2);
    }
    else if (impedanceConfig.type === StripType.SYMMETRIC_STRIPLINE) {
        return round(symetricStripline(impedanceConfig).zo, 2);
    }
    throw new Error("Invalid strip type");
}
//# sourceMappingURL=findWidthAndSpacing.js.map